import { SubRouteModelEnum } from '@albatrosdeveloper/ave-models-package/lib/enums/sub-route-model.enum';
import { t, tEnum } from 'app/core/i18n/i18n';
import { TranslationKey } from 'app/core/i18n/translation-keys';
import {
  TripCreationDayStatus,
  TripCreationItinerarySource,
  TripCreationPreviewDay,
  TripCreationPreviewReason,
  TripCreationPreviewResult,
} from 'app/modules/components/sales/trip/trip-creation-preview.types';

/** Sub-route models the "Crear viajes por itinerario" action applies to (FS-290). */
export const CREATE_TRIPS_FROM_ITINERARY_MODELS: readonly SubRouteModelEnum[] = [
  SubRouteModelEnum.AUTOVENTA,
  SubRouteModelEnum.ENTREGA,
];

export const CREATE_TRIPS_PREVIEW_REASON_LABEL_KEYS: Record<TripCreationPreviewReason, TranslationKey> = {
  [TripCreationPreviewReason.SUB_ROUTE_INACTIVE]: 'routing.SUB_ROUTES.CREATE_TRIPS_PREVIEW.REASONS.SUB_ROUTE_INACTIVE',
  [TripCreationPreviewReason.MODEL_NOT_APPLICABLE]: 'routing.SUB_ROUTES.CREATE_TRIPS_PREVIEW.REASONS.MODEL_NOT_APPLICABLE',
  [TripCreationPreviewReason.MISSING_MAIN_COLLABORATOR]: 'routing.SUB_ROUTES.CREATE_TRIPS_PREVIEW.REASONS.MISSING_MAIN_COLLABORATOR',
  [TripCreationPreviewReason.MISSING_RESPONSIBLE]: 'routing.SUB_ROUTES.CREATE_TRIPS_PREVIEW.REASONS.MISSING_RESPONSIBLE',
  [TripCreationPreviewReason.MISSING_SALES_OFFICE]: 'routing.SUB_ROUTES.CREATE_TRIPS_PREVIEW.REASONS.MISSING_SALES_OFFICE',
  [TripCreationPreviewReason.MISSING_ROUTE_GROUP]: 'routing.SUB_ROUTES.CREATE_TRIPS_PREVIEW.REASONS.MISSING_ROUTE_GROUP',
  [TripCreationPreviewReason.VEHICLE_OUT_OF_SERVICE]: 'routing.SUB_ROUTES.CREATE_TRIPS_PREVIEW.REASONS.VEHICLE_OUT_OF_SERVICE',
  [TripCreationPreviewReason.NO_ITINERARY]: 'routing.SUB_ROUTES.CREATE_TRIPS_PREVIEW.REASONS.NO_ITINERARY',
  [TripCreationPreviewReason.NO_ACTIVE_ITINERARY]: 'routing.SUB_ROUTES.CREATE_TRIPS_PREVIEW.REASONS.NO_ACTIVE_ITINERARY',
  [TripCreationPreviewReason.NO_CHARGE_ACTION]: 'routing.SUB_ROUTES.CREATE_TRIPS_PREVIEW.REASONS.NO_CHARGE_ACTION',
  [TripCreationPreviewReason.INCOMPLETE_SEQUENCE]: 'routing.SUB_ROUTES.CREATE_TRIPS_PREVIEW.REASONS.INCOMPLETE_SEQUENCE',
  [TripCreationPreviewReason.TRIP_ALREADY_EXISTS]: 'routing.SUB_ROUTES.CREATE_TRIPS_PREVIEW.REASONS.TRIP_ALREADY_EXISTS',
  [TripCreationPreviewReason.OVERLAPS_OTHER_DAY]: 'routing.SUB_ROUTES.CREATE_TRIPS_PREVIEW.REASONS.OVERLAPS_OTHER_DAY',
};

export const CREATE_TRIPS_PREVIEW_SOURCE_LABEL_KEYS: Record<TripCreationItinerarySource, TranslationKey> = {
  [TripCreationItinerarySource.SUB_ROUTE_ALTERNATIVE]: 'routing.SUB_ROUTES.CREATE_TRIPS_PREVIEW.SOURCES.SUB_ROUTE_ALTERNATIVE',
  [TripCreationItinerarySource.SUB_ROUTE]: 'routing.SUB_ROUTES.CREATE_TRIPS_PREVIEW.SOURCES.SUB_ROUTE',
  [TripCreationItinerarySource.ROUTE_GROUP_ALTERNATIVE]: 'routing.SUB_ROUTES.CREATE_TRIPS_PREVIEW.SOURCES.ROUTE_GROUP_ALTERNATIVE',
  [TripCreationItinerarySource.ROUTE_GROUP]: 'routing.SUB_ROUTES.CREATE_TRIPS_PREVIEW.SOURCES.ROUTE_GROUP',
};

/** Reasons caused by data the user edits in the sub-route form: ask to save and retry. */
export const CREATE_TRIPS_PREVIEW_SAVE_FIRST_REASONS: ReadonlySet<TripCreationPreviewReason> = new Set([
  TripCreationPreviewReason.MISSING_MAIN_COLLABORATOR,
  TripCreationPreviewReason.MISSING_RESPONSIBLE,
  TripCreationPreviewReason.MODEL_NOT_APPLICABLE,
  TripCreationPreviewReason.MISSING_ROUTE_GROUP,
]);

/** Per-day reasons that come from the itinerary content: say which itinerary was applied. */
const ITINERARY_CONTENT_REASONS: ReadonlySet<TripCreationPreviewReason> = new Set([
  TripCreationPreviewReason.NO_CHARGE_ACTION,
  TripCreationPreviewReason.INCOMPLETE_SEQUENCE,
]);

/** True when the create request must NOT be sent. */
export function isCreateTripsPreviewBlocked(result: TripCreationPreviewResult): boolean {
  return !!result.blockingReason || result.willCreate === 0;
}

/**
 * Blocking-dialog message (HTML, rendered by the confirmation modal through innerHTML).
 * Every interpolated value is escaped.
 */
export function buildCreateTripsBlockedMessage(result: TripCreationPreviewResult): string {
  if (result.blockingReason) {
    const reason = escapeHtml(reasonLabel(result.blockingReason));
    return CREATE_TRIPS_PREVIEW_SAVE_FIRST_REASONS.has(result.blockingReason)
      ? `<p>${reason}</p><p>${escapeHtml(t('routing.SUB_ROUTES.CREATE_TRIPS_PREVIEW.SAVE_FIRST_HINT'))}</p>`
      : `<p>${reason}</p>`;
  }

  const intro = t('routing.SUB_ROUTES.CREATE_TRIPS_PREVIEW.NO_TRIPS_INTRO', { days: result.days.length });
  const items = groupSkippedDaysByReason(result.days).map(([reason, days]) => {
    const dates = days.map((d) => formatDay(d.date)).join(', ');
    let line = `<strong>${escapeHtml(reasonLabel(reason))}</strong>: ${escapeHtml(dates)}`;
    if (ITINERARY_CONTENT_REASONS.has(reason)) {
      const sources = unique(days.map((d) => tEnum(CREATE_TRIPS_PREVIEW_SOURCE_LABEL_KEYS, d.itinerarySource)));
      if (sources.length) line += ` (${escapeHtml(sources.join(', '))})`;
    }
    if (reason === TripCreationPreviewReason.TRIP_ALREADY_EXISTS) {
      const ids = unique(days.flatMap((d) => (d.conflictingTripIds ?? []).map(String)));
      if (ids.length) {
        line += ` — ${escapeHtml(t('routing.SUB_ROUTES.CREATE_TRIPS_PREVIEW.EXISTING_TRIPS', { ids: ids.join(', ') }))}`;
      }
    }
    return `<li>${line}</li>`;
  });
  return `<p>${escapeHtml(intro)}</p><ul class="list-disc pl-5 mt-2">${items.join('')}</ul>`;
}

/** Confirmation text: how many trips and their load dates. */
export function buildCreateTripsConfirmMessage(result: TripCreationPreviewResult): string {
  const dates = result.days
    .filter((d) => d.status === TripCreationDayStatus.WILL_CREATE)
    .map((d) => formatDay(d.date))
    .join(', ');
  return escapeHtml(t('routing.SUB_ROUTES.CREATE_TRIPS_PREVIEW.CONFIRM_WITH_COUNT', { count: result.willCreate, dates }));
}

function reasonLabel(reason: TripCreationPreviewReason): string {
  return tEnum(CREATE_TRIPS_PREVIEW_REASON_LABEL_KEYS, reason) ?? t('routing.SUB_ROUTES.CREATE_TRIPS_PREVIEW.UNKNOWN_REASON');
}

function groupSkippedDaysByReason(
  days: TripCreationPreviewDay[],
): Array<[TripCreationPreviewReason, TripCreationPreviewDay[]]> {
  const groups = new Map<TripCreationPreviewReason, TripCreationPreviewDay[]>();
  for (const day of days) {
    if (day.status !== TripCreationDayStatus.SKIP || !day.reason) continue;
    groups.set(day.reason, [...(groups.get(day.reason) ?? []), day]);
  }
  return [...groups.entries()];
}

/** 'YYYY-MM-DD' → 'DD/MM' (any other format is returned as is). */
function formatDay(isoDate: string): string {
  const [, month, day] = (isoDate ?? '').split('-');
  return month && day ? `${day}/${month}` : (isoDate ?? '');
}

/** Escapes a value interpolated into the modal's HTML description. */
function escapeHtml(value: string): string {
  return (value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function unique(values: Array<string | undefined>): string[] {
  return [...new Set(values.filter((v): v is string => !!v))];
}
